import { createId } from "@/lib/ids";
import type { VectorEdge, VectorNetwork, VectorVertex } from "@/document/model/vectorNetwork";
import type { Point } from "./pathGeometry";
import { arcToCubics } from "./svgArcToCubics";

export interface PathDataToVectorNetworkOptions {
  reuseVertices?: boolean;
  onApproximatedCommand?: (command: string) => void;
}

export function pathDataToVectorNetwork(
  pathData: string,
  options: PathDataToVectorNetworkOptions = {},
): VectorNetwork {
  const tokens = pathData.match(/[a-zA-Z]|[-+]?(?:\d*\.\d+|\d+)(?:e[-+]?\d+)?/gi) ?? [];
  const vertices: VectorVertex[] = [];
  const edges: VectorEdge[] = [];
  let command = "";
  let index = 0;
  let cursor: VectorVertex | undefined;
  let subpathStart: VectorVertex | undefined;
  let previousControl: Point | undefined;
  let previousCommand = "";

  const readNumber = () => Number(tokens[index++] ?? 0);
  const hasNumber = () => index < tokens.length && !/^[a-zA-Z]$/.test(tokens[index]!);
  const readFlag = () => readNumber() !== 0;
  const cursorPoint = (): Point => ({ x: cursor?.x ?? 0, y: cursor?.y ?? 0 });
  const absolutize = (point: Point, relative: boolean): Point => ({
    x: relative && cursor ? cursor.x + point.x : point.x,
    y: relative && cursor ? cursor.y + point.y : point.y,
  });
  const vertex = (point: Point, localReuse = true): VectorVertex => {
    if (options.reuseVertices !== false) {
      const existing = vertices.find(candidate => distance(candidate, point) <= 0.001);
      if (existing) {
        return existing;
      }
    } else if (localReuse) {
      if (cursor && distance(cursor, point) <= 0.001) {
        return cursor;
      }
      if (subpathStart && distance(subpathStart, point) <= 0.001) {
        return subpathStart;
      }
    }
    const next = { id: createId("vertex"), x: point.x, y: point.y };
    vertices.push(next);
    return next;
  };
  const lineTo = (to: VectorVertex): void => {
    if (cursor && cursor.id !== to.id) {
      edges.push(lineEdge(cursor, to));
    }
    cursor = to;
    previousControl = undefined;
  };
  const cubicTo = (control1: Point, control2: Point, to: VectorVertex): void => {
    if (cursor && cursor.id !== to.id) {
      edges.push(cubicEdge(cursor, to, control1, control2));
    }
    cursor = to;
    previousControl = control2;
  };

  while (index < tokens.length) {
    if (/^[a-zA-Z]$/.test(tokens[index]!)) {
      command = tokens[index++]!;
    }

    switch (command) {
      case "M":
      case "m": {
        const relative = command === "m";
        let first = true;
        while (hasNumber()) {
          const next = vertex(absolutize({ x: readNumber(), y: readNumber() }, relative), !first);
          if (first || !cursor) {
            cursor = next;
            subpathStart = next;
            first = false;
          } else {
            lineTo(next);
          }
          command = relative ? "l" : "L";
        }
        previousCommand = command;
        break;
      }
      case "L":
      case "l": {
        const relative = command === "l";
        while (hasNumber()) {
          lineTo(vertex(absolutize({ x: readNumber(), y: readNumber() }, relative)));
        }
        previousCommand = command;
        break;
      }
      case "H":
      case "h": {
        const relative = command === "h";
        while (hasNumber()) {
          const x = readNumber();
          lineTo(vertex({ x: relative && cursor ? cursor.x + x : x, y: cursor?.y ?? 0 }));
        }
        previousCommand = command;
        break;
      }
      case "V":
      case "v": {
        const relative = command === "v";
        while (hasNumber()) {
          const y = readNumber();
          lineTo(vertex({ x: cursor?.x ?? 0, y: relative && cursor ? cursor.y + y : y }));
        }
        previousCommand = command;
        break;
      }
      case "C":
      case "c": {
        const relative = command === "c";
        while (hasNumber()) {
          const c1 = absolutize({ x: readNumber(), y: readNumber() }, relative);
          const c2 = absolutize({ x: readNumber(), y: readNumber() }, relative);
          const to = vertex(absolutize({ x: readNumber(), y: readNumber() }, relative));
          cubicTo(c1, c2, to);
        }
        previousCommand = command;
        break;
      }
      case "S":
      case "s": {
        const relative = command === "s";
        while (hasNumber()) {
          const reflected = reflectControl(previousControl, cursorPoint(), previousCommand);
          const c2 = absolutize({ x: readNumber(), y: readNumber() }, relative);
          const to = vertex(absolutize({ x: readNumber(), y: readNumber() }, relative));
          cubicTo(reflected, c2, to);
        }
        previousCommand = command;
        break;
      }
      case "Q":
      case "q": {
        const relative = command === "q";
        while (hasNumber()) {
          const control = absolutize({ x: readNumber(), y: readNumber() }, relative);
          const to = vertex(absolutize({ x: readNumber(), y: readNumber() }, relative));
          if (cursor) {
            cubicTo(
              { x: cursor.x + (2 / 3) * (control.x - cursor.x), y: cursor.y + (2 / 3) * (control.y - cursor.y) },
              { x: to.x + (2 / 3) * (control.x - to.x), y: to.y + (2 / 3) * (control.y - to.y) },
              to,
            );
            previousControl = control;
          } else {
            cursor = to;
          }
          options.onApproximatedCommand?.(command);
        }
        previousCommand = command;
        break;
      }
      case "T":
      case "t": {
        const relative = command === "t";
        while (hasNumber()) {
          const control = reflectControl(previousControl, cursorPoint(), previousCommand);
          const to = vertex(absolutize({ x: readNumber(), y: readNumber() }, relative));
          if (cursor) {
            cubicTo(
              { x: cursor.x + (2 / 3) * (control.x - cursor.x), y: cursor.y + (2 / 3) * (control.y - cursor.y) },
              { x: to.x + (2 / 3) * (control.x - to.x), y: to.y + (2 / 3) * (control.y - to.y) },
              to,
            );
            previousControl = control;
          } else {
            cursor = to;
          }
          options.onApproximatedCommand?.(command);
        }
        previousCommand = command;
        break;
      }
      case "A":
      case "a": {
        const relative = command === "a";
        while (hasNumber()) {
          const rx = readNumber();
          const ry = readNumber();
          const rotation = readNumber();
          const largeArc = readFlag();
          const sweep = readFlag();
          const target = absolutize({ x: readNumber(), y: readNumber() }, relative);
          const from = cursorPoint();
          for (const segment of arcToCubics(from, rx, ry, rotation, largeArc, sweep, target)) {
            cubicTo(segment.control1, segment.control2, vertex(segment.to));
          }
          options.onApproximatedCommand?.(command);
        }
        previousCommand = command;
        break;
      }
      case "Z":
      case "z": {
        if (cursor && subpathStart && cursor.id !== subpathStart.id) {
          edges.push(lineEdge(cursor, subpathStart));
        }
        cursor = subpathStart;
        previousControl = undefined;
        subpathStart = undefined;
        command = "";
        previousCommand = command;
        break;
      }
      default:
        index += 1;
    }
  }

  return {
    vertices,
    edges,
    regions: [],
    fillRule: "nonzero",
  };
}

export function contoursToVectorNetwork(contours: Point[][]): VectorNetwork {
  const vertices: VectorVertex[] = [];
  const edges: VectorEdge[] = [];

  for (const contour of contours) {
    const contourVertices = contour
      .filter((point, pointIndex) => pointIndex === 0 || distance(point, contour[pointIndex - 1]!) > 0.001)
      .map(point => ({ id: createId("vertex"), x: point.x, y: point.y }));
    if (contourVertices.length < 3) {
      continue;
    }

    const first = contourVertices[0]!;
    const last = contourVertices.at(-1)!;
    const closedVertices = distance(first, last) <= 0.001 ? contourVertices.slice(0, -1) : contourVertices;
    if (closedVertices.length < 3) {
      continue;
    }

    vertices.push(...closedVertices);
    for (let vertexIndex = 0; vertexIndex < closedVertices.length; vertexIndex += 1) {
      edges.push({
        id: createId("edge"),
        from: { vertexId: closedVertices[vertexIndex]!.id },
        to: { vertexId: closedVertices[(vertexIndex + 1) % closedVertices.length]!.id },
        directed: false,
      });
    }
  }

  return {
    vertices,
    edges,
    regions: [],
    fillRule: "evenodd",
  };
}

function reflectControl(previousControl: Point | undefined, current: Point, previousCommand: string): Point {
  if (previousControl && (previousCommand === "C" || previousCommand === "c" || previousCommand === "S" || previousCommand === "s" || previousCommand === "Q" || previousCommand === "q" || previousCommand === "T" || previousCommand === "t")) {
    return {
      x: current.x * 2 - previousControl.x,
      y: current.y * 2 - previousControl.y,
    };
  }
  return current;
}

function lineEdge(from: VectorVertex, to: VectorVertex): VectorEdge {
  return {
    id: createId("edge"),
    from: { vertexId: from.id },
    to: { vertexId: to.id },
    directed: false,
  };
}

function cubicEdge(from: VectorVertex, to: VectorVertex, fromHandle: Point, toHandle: Point): VectorEdge {
  return {
    id: createId("edge"),
    from: { vertexId: from.id, handle: { ...fromHandle } },
    to: { vertexId: to.id, handle: { ...toHandle } },
    directed: false,
  };
}

function distance(left: Point | VectorVertex, right: Point | VectorVertex): number {
  return Math.hypot(left.x - right.x, left.y - right.y);
}
